<template>
  <UiDialog
    class="ImageButton"
    :open.sync="isOpen"
    :close-scrim="false"
  >
    <template #trigger>
      <UiIcon
        :title="$t('ImageButton.title')"
        value="mdi:image-outline"
        class="ToolbarButton"
        :class="{ 'is-active': isActive }"
      ></UiIcon>
    </template>

    <template #contents>
      <h1>{{ $t('ImageButton.title') }}</h1> <br>

      <div style="gap: 15px; display: flex; align-items: center;">
        <UiInput
          type="text"
          v-model="img.src"
          :label="$t('ImageButton.link')"
          @input="setUrl(img.src)"
        ></UiInput>

        <UiIcon v-if="!img.src" style="cursor: pointer;" value="mdi:folder-search-outline" @click="openFileInput" />
      </div>

      <div v-if="img.src">
        <h3>{{ $t('ImageButton.dimensions') }}</h3>
        <div style="display: flex; gap: 15px;">
          <UiInput
            type="text"
            v-model="img.width"
            :label="$t('ImageButton.width')"
            @change="onchangeWidth"
          ></UiInput>
          <UiInput
            type="text"
            v-model="img.height"
            :label="$t('ImageButton.height')"
            @change="onchangeHeight"
          ></UiInput>
        </div>

        <UiInput
          :label="$t('ImageButton.restrictDimensions')"
          type="checkbox"
          v-model="restrictDimensions"
        ></UiInput>

        <div style="display: flex; gap: 15px; margin-bottom: 30px;">
          <div>{{ $t('ImageButton.rotateImage') }}</div>
          <UiIcon style="cursor: pointer;" value="mdi:rotate-left" @click="rotate('left')" />
          <UiIcon style="cursor: pointer;" value="mdi:rotate-right" @click="rotate('right')" />
          <div>|</div>
          <UiIcon style="cursor: pointer;" value="mdi:horizontal-rotate-counterclockwise" @click="rotate('horizontal')" />
          <UiIcon style="cursor: pointer;" value="mdi:axis-z-rotate-clockwise" @click="rotate('vertical')" />
        </div>
      </div>

      <img v-if="img.src" :src= "img.src" width="40%" height="40%" :style="'transform:'+img.descriptionRotation+';'">

      <FilePicker
        v-if="isOpenFolder"
        @select="onSelectFile"
      ></FilePicker>
    </template>

    <template #footer>
      <button
        type="button"
        class="ui-native --main"
        @click="onAccept"
      >{{ $t('ImageButton.Accept') }}</button>
      <button
        type="button"
        class="ui-native --cancel"
        @click="onCancel"
      >{{ $t('ImageButton.Cancel') }}</button>
    </template>
  </UiDialog>
</template>

<script>
import useI18n from '@/modules/i18n/mixins/useI18n.js';
import { UiIcon, UiDialog, UiInput } from '@/modules/ui/components';
import FilePicker from './FilePicker.vue';

export default {
  components: {
    FilePicker,
    UiDialog,
    UiIcon,
    UiInput,
  },

  mixins: [useI18n],

  props: {
    editor: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      isOpen: false,
      img: {
        src: '',
        alt: '',
        title: '',
        style: '',
        width: '',
        height: '',
        originalWidth: '',
        originalHeight: '',
        rotation: 0,
        descriptionRotation: '',
      },
      isOpenFolder: false,
      restrictDimensions: false,
    };
  },

  methods: {
    onchangeWidth() {
      if(this.img.width && this.restrictDimensions) {
        let operation1 = (this.img.originalHeight / this.img.originalWidth) * this.img.width;
        this.img.height = operation1 ? Math.round(operation1) : 0;
      }
    },

    onchangeHeight() {
      if(this.img.height && this.restrictDimensions) {
        let operation = ((this.img.originalWidth / this.img.originalHeight) * this.img.height)
        this.img.width = operation ? Math.round(operation) : 0;
      }
    },

    onSelectFile(objFile) {
      this.setUrl(objFile.url);
      this.isOpenFolder = false;
    },

    onAccept() {
      this.img.descriptionRotation = this.img.rotation ? this.img.descriptionRotation : 'rotate(0deg)';

      this.editor.chain().focus().setImage({ src: this.img.src, width: this.img.width, height: this.img.height, style: "transform:"+this.img.descriptionRotation+";" }).run();

      this.img = {
        src: '',
        alt: '',
        title: '',
        style: '',
        width: '',
        height: '',
        originalWidth: '',
        originalHeight: '',
        rotation: 0,
        descriptionRotation: '',
      },

      this.isOpen = false;
    },

    onCancel() {
      this.img = {
        src: '',
        alt: '',
        title: '',
        style: '',
        width: '',
        height: '',
        originalWidth: '',
        originalHeight: '',
        rotation: 0,
        descriptionRotation: '',
      },

      this.isOpen = false;
    },

    setUrl(url) {
      if (!url) {
        return;
      }

      const image_tmp = new Image();
      image_tmp.onload = () => {
        this.img.width = image_tmp.width;
        this.img.originalWidth = image_tmp.width;
        this.img.height = image_tmp.height;
        this.img.originalHeight = image_tmp.height;
        this.restrictDimensions = true;
      };
      image_tmp.src = url;

      this.img.src = url;
    },

    openFileInput() {
      this.isOpenFolder = true;
    },

    rotate(event) {
      if (event === "right") {
        this.img.rotation = this.img.rotation + 90;
        this.img.descriptionRotation = 'rotate('+this.img.rotation+'deg)'
      }

      if (event === "left") {
        this.img.rotation = this.img.rotation - 90;
        this.img.descriptionRotation = 'rotate('+this.img.rotation+'deg)'
      }

      if (event === "horizontal") {
        if (this.img.rotation === -1) {
          this.img.rotation = 1;
          this.img.descriptionRotation = 'scaleY(1)'
        } else {
          this.img.rotation = -1;
          this.img.descriptionRotation = 'scaleY(-1)'
        }
      }

      if (event === "vertical") {
        if (this.img.rotation === -1) {
          this.img.rotation = 1;
          this.img.descriptionRotation = 'scaleX(1)'
        } else {
          this.img.rotation = -1;
          this.img.descriptionRotation = 'scaleX(-1)'
        }
      }
    },
  },

  computed: {
    currentImage() {
      const img = this.editor.getAttributes('image');

      if (img?.src) {
        var text = img.style ? img.style.split(':') : ['', ''];
        if (/rotate\(-?\d+deg\);/.test(text[1])) {
          var numeroExtraido = text[1].match(/rotate\((-?\d+)deg\);/);
        } else if (/scaleY\((-?\d+)\);/.test(text[1])) {
          var numeroExtraido = text[1].match(/scaleY\((-?\d+)\);/);
        } else if (/scaleX\((-?\d+)\);/.test(text[1])) {
          var numeroExtraido = text[1].match(/scaleX\((-?\d+)\);/);
        } else {
          var numeroExtraido = ['rotate(0deg);', '0'];
        }

        img.descriptionRotation = numeroExtraido[0];
        img.rotation = parseInt(numeroExtraido[1]);
        img.originalHeight = img.height;
        img.originalWidth = img.width;

        return img;
      }
      return {
        src: '',
        alt: '',
        title: '',
        style: '',
        width: '',
        height: '',
        originalWidth: '',
        originalHeight: '',
        rotation: 0,
        descriptionRotation: '',
      };
    },

    isActive() {
      return this.editor.isActive('image');
    },
  },

  watch: {
    currentImage(newValue) {
      this.img = newValue;
    },
  },

  i18n: {
    en: {
        'ImageButton.link': 'Link',
        'ImageButton.width': 'Width',
        'ImageButton.height': 'Height',
        'ImageButton.Cancel': 'Cancel',
        'ImageButton.Accept': 'Accept',
        'ImageButton.dimensions': 'Dimensions',
        'ImageButton.title': 'Insert/edit image',
        'ImageButton.rotateImage': 'Rotate image',
        'ImageButton.restrictDimensions': 'Restrict dimensions',
      },

      es: {
        'ImageButton.link': 'Enlace',
        'ImageButton.width': 'Ancho',
        'ImageButton.height': 'Alto',
        'ImageButton.Cancel': 'Cancelar',
        'ImageButton.Accept': 'Aceptar',
        'ImageButton.dimensions': 'Dimensiones',
        'ImageButton.title': 'Insertar/editar imagen',
        'ImageButton.rotateImage': 'Girar imagen',
        'ImageButton.restrictDimensions': 'Restringir dimensiones',
      },

      de: {
        'ImageButton.link': 'Link',
        'ImageButton.width': 'Breite',
        'ImageButton.height': 'Höhe',
        'ImageButton.Cancel': 'Abbrechen',
        'ImageButton.Accept': 'Akzeptieren',
        'ImageButton.dimensions': 'Abmessungen',
        'ImageButton.title': 'Bild einfügen/bearbeiten',
        'ImageButton.rotateImage': 'Bild rotieren',
        'ImageButton.restrictDimensions': 'Abmessungen einschränken',
      },
  },
};
</script>

<style lang="scss">
.ImageButton {
  .ui-dialog-container {
    position: static;
    max-width: 600px !important;
  }
  .ui-field {
    width: 100%;
    margin-bottom: 10px;
    input {
      width: 100%;
    }
  }
  &__preview {
    img {
      max-width: 100%;
      height: auto;
      max-height: 300px;
    }
  }
}
</style>